import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { mediaApi } from "./mediaApi";
import { formatDuration } from "./media.utils";

const REFRESH_MARGIN_MS = 30000;

export const isTrustedPreviewUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const SecureMediaPreview = ({ assetType, media, compact = false }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);
  const automaticRetryRef = useRef(0);
  const mediaId = media?._id;
  const verifiedAt = assetType === "video" ? media?.video?.verifiedAt : media?.poster?.verifiedAt;
  const expiresAt = preview?.expiresAt;

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const loadPreview = useCallback(async ({ automatic = false } = {}) => {
    if (!mediaId || !verifiedAt) return;
    if (automatic && automaticRetryRef.current >= 1) {
      setError("Preview authorization expired. Select Retry preview to request a new one.");
      return;
    }
    if (automatic) automaticRetryRef.current += 1;
    setLoading(true);
    setError("");
    try {
      const { preview: next } = await mediaApi.preview(mediaId, assetType);
      if (!isTrustedPreviewUrl(next?.url)) {
        throw new Error("The server returned an unsafe preview URL.");
      }
      if (mountedRef.current) setPreview(next);
    } catch (requestError) {
      if (mountedRef.current) {
        setPreview(null);
        setError(requestError.message || "Secure preview unavailable.");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [assetType, mediaId, verifiedAt]);

  useEffect(() => {
    setPreview(null);
    setError("");
    automaticRetryRef.current = 0;
    if (mediaId && verifiedAt) loadPreview();
  }, [loadPreview, mediaId, verifiedAt]);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const delay = Math.max(new Date(expiresAt).getTime() - Date.now() - REFRESH_MARGIN_MS, 1000);
    const timer = window.setTimeout(() => loadPreview({ automatic: true }), delay);
    return () => window.clearTimeout(timer);
  }, [expiresAt, loadPreview]);

  const refreshAfterPlaybackError = () => loadPreview({ automatic: true });
  const orientation = preview?.orientation || (media?.kind === "short" ? "portrait" : "landscape");
  const ratio = preview?.width && preview?.height
    ? `${preview.width} / ${preview.height}`
    : media?.kind === "short" ? "9 / 16" : "16 / 9";

  if (!verifiedAt) {
    return <Box className="secure-media-preview secure-media-preview--empty">No verified {assetType} preview yet.</Box>;
  }
  return (
    <Box className={`secure-media-preview secure-media-preview--${orientation}${compact ? " secure-media-preview--compact" : ""}`}>
      {loading && !preview && (
        <Box className="secure-media-preview-loading"><CircularProgress size={22} /><span>Loading secure {assetType} preview…</span></Box>
      )}
      {error && (
        <Alert severity="warning" action={<Button size="small" startIcon={<Refresh />} onClick={() => { automaticRetryRef.current = 0; loadPreview(); }}>Retry preview</Button>}>
          {error}
        </Alert>
      )}
      {preview && (
        <>
          <Box className="secure-media-preview-frame" style={{ aspectRatio: ratio }}>
            {assetType === "video" ? (
              <video key={preview.url} controls preload="metadata" onError={refreshAfterPlaybackError} aria-label={`Secure preview of ${media.title || "media"}`}>
                <source src={preview.url} type={preview.mimeType || "video/mp4"} />
                Your browser does not support video playback.
              </video>
            ) : (
              <img key={preview.url} src={preview.url} onError={refreshAfterPlaybackError} alt={media.poster?.alt || `Poster for ${media.title || "media"}`} />
            )}
          </Box>
          <Box className="secure-media-preview-meta">
            <Typography variant="caption"><strong>Verified</strong></Typography>
            <Typography variant="caption">{preview.width || "—"}×{preview.height || "—"}</Typography>
            {assetType === "video" && <Typography variant="caption">{formatDuration(preview.durationSeconds)}</Typography>}
            {assetType === "poster" && preview.conversion?.status === "converted" && (
              <Typography variant="caption">9:16 crop · {preview.conversion.focalPoint || "center"}</Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SecureMediaPreview;
